import { AuthedMultiProductResponse, ProductIdentity } from "@/@types/data/collections";
import React from "react";
import { ProductGame, PurchasabilityStatus } from "@/@types/common";
import { useCreator } from "./useCreator";
import { GetRequest, PostRequest, useRequest } from "./useRequests";
import { useXSSOAuth } from "./useXSSOAuth";
import { convertProductType } from "./utils";

export type ProductInfoData = {
  id: string | null;
  game: ProductGame;
  type: string;
  cost: number | null;
  price: number | null;
  status: PurchasabilityStatus | undefined;
  itemPriceInBundle?: number | null;
};

export const useProductInfo = (identities: ProductIdentity[], forceUnauthed = false, wait = false) => {
  const { loading, isLoggedIn, login } = useXSSOAuth();
  const { trafficSource } = useCreator(undefined, true);
  const [productRequest, setProductRequest] = React.useState<GetRequest | PostRequest>({} as GetRequest | PostRequest);
  let requestOptions = {} as GetRequest | PostRequest; // Using "as" here as {} isn't strictly compatible, but simplifys the type later on.
  const productQuotesUrl = trafficSource ? `/v3/orders/quote?affiliate=${trafficSource}` : "/v3/orders/quote";

  React.useEffect(() => {
    // Using wait as a way to defer the api call (used to load products when they appear in the viewport)
    // Needed because react forces us not to use hooks inside callbacks (so we cannot perform this call after a callback)
    if (!wait && !loading && identities.length > 0) {
      // Ensure identities are called with uppercased gameId's
      const productIdentities = identities.map((ident) => ({
        ...ident,
        productType: convertProductType(ident.productType),
        gameId: ident.gameId.toLowerCase(),
      }));
      // Player is not logged in
      if (!isLoggedIn) {
        // TODO: remove it when we will be able to track click events from the Web
        // getProduct call to track clicks before we can send them from the Web
        if (productIdentities.length == 1) {
          const gameName = productIdentities[0].gameId.toLowerCase();
          const productType = productIdentities[0].productType;
          const productId = productIdentities[0].productId;
          const productUrl = trafficSource
            ? `/v3/products/${gameName}/${productType}/${productId}?affiliate=${trafficSource}`
            : `/v3/products/${gameName}/${productType}/${productId}`;
          requestOptions = {
            method: "GET",
            url: productUrl,
          };
        } else {
          requestOptions = {
            method: "POST",
            url: "/v3/products/",
            data: productIdentities,
          };
        }
      }
      // Player is logged in
      if (isLoggedIn && !forceUnauthed) {
        // Do we need rsoReady^ above?
        requestOptions = {
          method: "POST",
          url: productQuotesUrl,
          data: productIdentities,
        };
      }
      setProductRequest(requestOptions);
    }
  }, [isLoggedIn, identities.length, forceUnauthed, wait, loading]);

  // Returns empty object while data is being fetched
  const { data: rawData, ...rest } = useRequest<AuthedMultiProductResponse>(productRequest);

  // useRequest starts with an empty request, which has no isLoading key, so when we use productsInfo.isLoading, we get false.
  // the reality is that the call hasn't happened yet while the hook is initialising.
  // setting isLoading to true when there is no url, means that we can use productsInfo.isLoading and get true until the call has been made.
  if (!productRequest.url) {
    return {
      data: { productsInfo: [], ownedItems: [] },
      ...rest,
      isLoading: true,
    };
  }

  if (rest.response?.status === 401) {
    login();
    return { data: { productsInfo: [], ownedItems: [] }, ...rest };
  }
  // using optional chaining here to simplify the deep check.
  const products = rawData?.products ?? [];
  const productsInfo: ProductInfoData[] = Object.values(products).map((product) => ({
    ...product,
    cost: product?.prices?.find((i) => i.finalPrice)?.finalPrice.cost ?? null,
    price: product?.prices?.find((i) => i.originalPrice)?.originalPrice.cost ?? null,
  }));
  const ownedItems = rawData?.ownedItems?.map((i) => i.productId);
  return {
    data: {
      productsInfo,
      ownedItems,
    },
    ...rest,
  };
};
